// src/services/tickets/ticketsService.js
import { ref, set, get, runTransaction } from "firebase/database";
import { rtdb } from "../firebase/rtdb";

function genId(prefix = "id") {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Cria um pedido completo no RTDB:
 *  - orders/{orderId}
 *  - tickets/{ticketId}  (um por ingresso individual)
 *  - ticketsByUser/{uid}/{ticketId}: true
 *  - incrementa ticketLots/{eventId}/{lotId}/soldCount
 *  - incrementa events/{eventId}/stats/soldCount e grossRevenue
 *
 * Valida limitPerUser antes de criar.
 */
export async function createOrder({ cart, buyer, paymentMethod, userId }) {
  const limitPerUser = cart.event?.limitPerUser ?? 2;
  const newCount = cart.items.reduce((acc, item) => acc + item.quantity, 0);
  const organizerId =
    cart.event?.createdByUid ||
    cart.event?.organizerId ||
    null;

  // ── verifica limite por usuário ────────────────────────────────────────────
  const indexSnap = await get(ref(rtdb, `ticketsByUser/${userId}`));
  let existingCount = 0;

  if (indexSnap.exists()) {
    const ids = Object.keys(indexSnap.val());
    const ticketSnaps = await Promise.all(
      ids.map((id) => get(ref(rtdb, `tickets/${id}`))),
    );
    existingCount = ticketSnaps.filter(
      (s) =>
        s.exists() &&
        s.val().eventId === cart.eventId &&
        s.val().status !== "cancelled",
    ).length;
  }

  if (existingCount + newCount > limitPerUser) {
    throw new Error(
      `Limite de ${limitPerUser} ingresso(s) por usuário atingido para este evento.`,
    );
  }

  // ── cria ingressos individuais ─────────────────────────────────────────────
  const orderId = genId("ord");
  const now = Date.now();
  const feePercent = cart.summary?.feePercent ?? 3;

  const tickets = [];
  for (const item of cart.items) {
    for (let i = 0; i < item.quantity; i++) {
      const ticketId = genId("tix");
      const unitFee = Number(item.unitPrice) * (feePercent / 100);

      tickets.push({
        ticketId,
        orderId,
        eventId: cart.eventId,
        organizerId,
        userId,
        ownerUid: userId,
        ticketTypeName: item.name || "",
        lotName: item.lot || "Lote 1",
        lotId: item.ticketId || "",   // item.ticketId == lotId (ex: uuid_lot_1)
        eventTitle: cart.event?.title || "",
        eventBannerUrl: cart.event?.bannerUrl || "",
        eventStartAtMs: cart.event?.startAtMs || null,
        eventCity: cart.event?.location?.city || "",
        eventState: cart.event?.location?.state || "",
        unitPrice: Number(item.unitPrice) || 0,
        feePercent,
        feeAmount: unitFee,
        totalPaid: Number(item.unitPrice) + unitFee,
        status: "valid",
        qrValue: `ONURB|${ticketId}|${orderId}|${cart.eventId}`,
        purchasedAt: now,
        checkinAt: null,
      });
    }
  }

  // ── escreve pedido ─────────────────────────────────────────────────────────
  await set(ref(rtdb, `orders/${orderId}`), {
    orderId,
    userId,
    eventId: cart.eventId,
    eventTitle: cart.event?.title || "",
    items: cart.items,
    summary: cart.summary,
    buyer,
    paymentMethod,
    ticketCount: tickets.length,
    status: "paid",
    createdAt: now,
  });

  // ── escreve ingressos, índice por usuário e índice por evento ───────────
  await Promise.all([
    ...tickets.flatMap((t) => [
      set(ref(rtdb, `tickets/${t.ticketId}`), t),
      set(ref(rtdb, `ticketsByUser/${userId}/${t.ticketId}`), true),
    ]),
    set(ref(rtdb, `ordersByEvent/${cart.eventId}/${orderId}`), true),
  ]);

  // ── atualiza contadores de vendas ─────────────────────────────────────────
  const lotCounts = {};
  let totalRevenue = 0;

  for (const item of cart.items) {
    const lotId = item.ticketId;
    lotCounts[lotId] = (lotCounts[lotId] || 0) + item.quantity;
    totalRevenue += item.lineTotal || 0;
  }

  await Promise.all([
    // soldCount por lote
    ...Object.entries(lotCounts).map(([lotId, count]) =>
      runTransaction(
        ref(rtdb, `ticketLots/${cart.eventId}/${lotId}/soldCount`),
        (current) => (current || 0) + count,
      ),
    ),
    // stats do evento
    runTransaction(
      ref(rtdb, `events/${cart.eventId}/stats/soldCount`),
      (c) => (c || 0) + tickets.length,
    ),
    runTransaction(
      ref(rtdb, `events/${cart.eventId}/stats/grossRevenue`),
      (c) => (c || 0) + totalRevenue,
    ),
  ]);

  return { orderId, tickets };
}
